import * as React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { LucideIcon } from './LucideIcon';
import type { Image as Interface, FileMetadata } from '../../design/components';
import { isValidImageSource, isFileMetadata, downloadImage } from '../shared/image';

export const Image: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'No image selected',
  hidden,
  inline,
  readonly: _readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['' as unknown as FileMetadata, () => {}];
  const [expanded, setExpanded] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const isValid = isValidImageSource(value);

  const displayUrl = React.useMemo(() => isValid ? (value as FileMetadata).url : '', [value, isValid]);

  React.useEffect(() => {
    return () => { if (displayUrl.startsWith('blob:')) URL.revokeObjectURL(displayUrl); };
  }, [displayUrl]);

  const handleDownload = () => {
    if (isFileMetadata(value)) downloadImage(value.url, value.name);
  };

  const processFile = async (file: File) => {
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      img.src = url;
      await img.decode();
      setValue({
        url, name: file.name, type: file.type, size: file.size,
        modified: new Date(file.lastModified).toISOString(),
        pages: 1, width: img.naturalWidth, height: img.naturalHeight, file,
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file).catch(() => {});
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file).catch(() => {});
  };

  // Inline mode: show small thumbnail
  if (inline) {
    return value ? (
      <img src={displayUrl} alt={name || 'Image'} className={cn('w-8 h-8 object-cover rounded', hidden && 'hidden')} />
    ) : null;
  }

  const canUpload = !_readonly && !disabled;

  return (
    <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
      {/* Label */}
      {name && (
        <Label htmlFor={name} className={cn(error && 'text-destructive')}>
          {name}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {/* Image Preview */}
      {isValid && (
        <div
          className={cn(
            'w-full border rounded-md bg-muted/30',
            expanded ? 'overflow-auto' : 'overflow-hidden',
            disabled && 'opacity-50'
          )}
          style={expanded ? { maxHeight: '80vh' } : undefined}
        >
          {/* Controls */}
          <div className="sticky top-0 z-10 flex items-center justify-center gap-1 py-2 border-b bg-background/80 backdrop-blur-sm">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setExpanded(!expanded)}
                >
                  <LucideIcon name={expanded ? 'Minimize' : 'Maximize'} size="small" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{expanded ? 'Shrink' : 'Expand'}</TooltipContent>
            </Tooltip>

            {isFileMetadata(value) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    asChild
                  >
                    <a href={value.url} target="_blank" rel="noopener noreferrer">
                      <LucideIcon name="ExternalLink" size="small" />
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open in new tab</TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={handleDownload}
                >
                  <LucideIcon name="Download" size="small" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download</TooltipContent>
            </Tooltip>

            {canUpload && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setValue('' as unknown as FileMetadata)}
                  >
                    <LucideIcon name="Trash2" size="small" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remove</TooltipContent>
              </Tooltip>
            )}
          </div>

          <div className="flex justify-center">
            <img
              src={displayUrl}
              alt={name || 'Image'}
              className={cn(
                'w-full object-contain',
                !expanded && 'max-h-[300px]'
              )}
            />
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isValid && canUpload && (
        <div
          className={cn(
            'flex flex-col items-center justify-center h-24 rounded-md border border-dashed bg-muted/50 text-muted-foreground cursor-pointer transition-colors',
            dragOver && 'border-primary bg-primary/5',
            error && 'border-destructive'
          )}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <LucideIcon name="Upload" size="large" className="mb-1" />
          <span className="text-sm">Click or drag to upload image</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      )}

      {/* Static empty state for readonly/disabled */}
      {!isValid && !canUpload && (
        <div
          className={cn(
            'flex items-center justify-center h-24 rounded-md border bg-muted/50 text-muted-foreground',
            error && 'border-destructive'
          )}
        >
          <LucideIcon name="Image" size="large" className="mr-2" />
          <span>{placeholder}</span>
        </div>
      )}

      {(error || description) && (
        <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
          {error || description}
        </p>
      )}
    </div>
  );
};
